import cn from 'classnames';
import { useContext } from 'react';

import { MOLAR_TOOTH_NUMBER, ToothStatus } from '@/shared/config';
import { blackAndWhiteToothImages } from '@/shared/config/blackAndWhiteToothImages';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { isThirdMolarTooth } from '@/entities/tooth';

import { ToothChartSize } from '../../config/types';
import { ToothChartContext } from '../ToothChartRoot/ToothChartRoot';

import styles from './ToothChartIcon.module.scss';
import toothSprite from './toothSprite.svg';

const statusStyles: Record<ToothStatus, string> = {
  healthy: styles.statusHealthy,
  unhealthy: styles.statusUnhealthy,
  missing: styles.statusMissing,
  treated: styles.statusTreated,
  lowProbability: styles.statusLowProbability,
  notFound: styles.notFound,
};

type IconSizeConfig = Record<ToothChartSize, { height: number; width: number }>;

const smallCommon = { height: 32, width: 14 };
const smallMolar = { height: 31, width: 17 };
const smallMissing = { height: 16, width: 12 };
const smallNotFound = { height: 21, width: 10 };

const sizes: {
  common: IconSizeConfig;
  molar: IconSizeConfig;
  missing: IconSizeConfig;
  notFound: IconSizeConfig;
} = {
  common: {
    default: {
      height: 48,
      width: 20,
    },
    small: smallCommon,
    mobile: smallCommon,
  },
  molar: {
    default: {
      height: 46,
      width: 26,
    },
    small: smallMolar,
    mobile: smallMolar,
  },
  missing: {
    default: {
      height: 12,
      width: 12,
    },
    small: smallMissing,
    mobile: smallMissing,
  },
  notFound: {
    default: {
      height: 17,
      width: 10,
    },
    small: smallNotFound,
    mobile: smallNotFound,
  },
};

type ToothChartIconProps = {
  className?: string;
  ISONumber: number;
  status: ToothStatus;
  blackAndWhite?: boolean;
};

export const ToothChartIcon = (props: ToothChartIconProps) => {
  const { className, ISONumber, status, blackAndWhite = false } = props;

  const size = useContext(ToothChartContext);

  const { isFDA } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const { width: missingWidth, height: missingHeight } = sizes.missing[size];
  const { width: notFoundWidth, height: notFoundHeight } = sizes.notFound[size];
  const { width: commonWidth, height: commonHeight } = sizes.common[size];
  const { width: molarWidth, height: molarHeight } = sizes.molar[size];

  const isMobile = size === 'mobile';
  const isMolar = MOLAR_TOOTH_NUMBER.includes(ISONumber);
  const isThirdMolar = isThirdMolarTooth(ISONumber);

  switch (status) {
    case 'missing': {
      return (
        <svg
          data-component="tooth-chart-icon"
          role="presentation"
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 12 12"
          width={isMobile ? '100%' : missingWidth}
          height={isMobile ? '100%' : missingHeight}
          style={
            isMobile
              ? {
                  maxWidth: missingWidth,
                  maxHeight: missingHeight,
                }
              : undefined
          }
          className={cn(
            styles.container,
            styles.nonToothIcon,
            styles.statusMissing,
            (isThirdMolar || isFDA) && styles.thirdMolar,
            blackAndWhite && styles.blackAndWhite,
          )}
        >
          <use href={`${toothSprite}#toothMissed`} />
        </svg>
      );
    }

    case 'notFound': {
      return (
        <svg
          data-component="tooth-chart-icon"
          role="presentation"
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 10 17"
          width={isMobile ? '100%' : notFoundWidth}
          height={isMobile ? '100%' : notFoundHeight}
          style={
            isMobile
              ? { maxWidth: notFoundWidth, maxHeight: notFoundHeight }
              : undefined
          }
          className={cn(
            styles.container,
            styles.nonToothIcon,
            styles.statusNotFound,
          )}
        >
          <use href={`${toothSprite}#toothNotFound`} />
        </svg>
      );
    }

    default: {
      return blackAndWhite ? (
        <img
          src={
            blackAndWhiteToothImages[
              status as Exclude<
                ToothStatus,
                ToothStatus.missing | ToothStatus.notFound
              >
            ][ISONumber]
          }
          alt={`tooth${ISONumber}`}
          role="presentation"
          aria-hidden="true"
          width={isMolar ? 27 : 20}
          height={isMolar ? 47 : 48}
          className={cn(className, styles.container)}
        />
      ) : (
        <svg
          data-component="tooth-chart-icon"
          role="presentation"
          aria-hidden="true"
          focusable="false"
          viewBox={isMolar ? '0 0 27 47' : '0 0 20 48'}
          width={isMobile ? '100%' : isMolar ? molarWidth : commonWidth}
          height={isMobile ? '100%' : isMolar ? molarHeight : commonHeight}
          style={
            isMobile
              ? {
                  maxWidth: isMolar ? molarWidth : commonWidth,
                  maxHeight: isMolar ? molarHeight : commonHeight,
                }
              : undefined
          }
          className={cn(className, styles.container, statusStyles[status])}
        >
          <use href={`${toothSprite}#tooth${ISONumber}`} />
        </svg>
      );
    }
  }
};
